/**
 * `<RichTextEditorPane>` — the markdown-canonical WYSIWYG editor.
 *
 * 2026-05-18: REPLACED MDXEditor with Tiptap (ProseMirror). The MDXEditor
 * implementation accumulated eight patches just to keep notes from silently
 * rendering blank — every new corpus subset (Joplin <style>, Joplin :/uuid
 * resource refs, Plaud serialised Python lists, the `## Original content`
 * HTML pattern, nested span+strikethrough combinations) hit MDXEditor's
 * mdast→Lexical conversion in a way that produced an empty Lexical doc
 * with no thrown error and no ErrorBoundary trip. For a PKM where invisible
 * content equals lost content, that failure mode was untenable.
 *
 * Tiptap pivots through HTML: markdown → marked → HTML → ProseMirror's
 * parseHTML → editor doc. ProseMirror's HTML parser is the same code
 * browsers use for contentEditable — when it hits an unknown construct it
 * coerces to the nearest schema-valid node (usually a text node containing
 * the visible characters). The result is never empty. Worst case is
 * "formatting looks weird," not "note vanishes."
 *
 * Architecture invariants preserved from the old implementation:
 *
 *   - Same props interface (`markdown` in, `onChange(markdown)` out) so
 *     `EditorSurface.tsx` needs no changes
 *   - Markdown remains canonical; the backend never sees ProseMirror state
 *   - Controlled-component contract: external markdown prop changes (e.g.
 *     user typed in CM6 in split mode) reseed the editor
 *   - The pair-with-`key={noteId}` pattern in EditorSurface still applies —
 *     cross-note navigation forces a clean remount so initial content is
 *     correct from the start
 *
 * Feature parity with the old toolbar:
 *
 *   Inline    Bold · Italic · Underline · Strikethrough · Sup/Sub · Inline code
 *   Blocks    Paragraph / h1–h6 / blockquote select
 *   Lists     Bulleted · Numbered · Task (checkbox)
 *   Inserts   Link · Image · Table · Horizontal rule · Code block
 *   History   Undo · Redo
 *
 * Code block syntax highlighting uses `lowlight` (highlight.js core) instead
 * of MDXEditor's CodeMirror-in-CodeMirror approach. Smaller bundle, simpler
 * mental model.
 *
 * Inline-style preservation: Joplin's `<span style="color: rgb(...)">…</span>`
 * patterns survive round-trip thanks to `@tiptap/extension-text-style` +
 * `@tiptap/extension-color`. The colour information lives in the markdown
 * body as raw inline HTML on the way back out via turndown — same shape as
 * the input, not silently dropped.
 */

import './richTextEditor.css';

import { useCallback, useEffect, useRef, useState, type CSSProperties } from 'react';
import { EditorContent, useEditor, type Editor } from '@tiptap/react';
import {
  ArrowDownToLine,
  ArrowLeftToLine,
  ArrowRightToLine,
  ArrowUpToLine,
  PanelTop,
  Trash2,
  X as XIcon,
} from 'lucide-react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import TextAlign from '@tiptap/extension-text-align';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import Link from '@tiptap/extension-link';
import { AttachmentImage } from './attachmentImageExtension';
import Table from '@tiptap/extension-table';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import { ResizableTableRow, TableRowResize } from './tableRowResize';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight';
import { common, createLowlight } from 'lowlight';
import { MermaidBlock } from '@/diagram/react/mermaidBlockExtension';

import {
  markdownToEditorHtml,
  editorHtmlToMarkdown,
} from './markdownRoundtrip';
import { QuoteHighlightExtension } from './quoteHighlightExtension';
import { HashtagDecoratorExtension } from './hashtagDecoratorExtension';
import {
  EntityDecoratorExtension,
  type EntityRef,
} from './entityDecoratorExtension';
import { HighlightMark } from './highlightMarkExtension';
import { SearchReplaceExtension } from './searchReplaceExtension';
import { FindReplaceBar } from './FindReplaceBar';
import {
  InlineTokenModal,
  type InlineTokenKind,
} from './InlineTokenModal';
import { InlineTokenContextMenu } from './InlineTokenContextMenu';
import { measurePerf } from './editorPerf';
import { TextSelection } from '@tiptap/pm/state';
import { WikilinkNode } from './wikilinkNodeExtension';
import { WikilinkSuggestion } from './wikilinkSuggestion';
import type { AutocompleteSourceStore } from './autocomplete/sources';
import { useNavigate } from 'react-router-dom';
import { toEditor } from '@/routes';
import { uploadAttachment, getNote } from '@/api';
import { asNoteId } from '@/types';
import type { NoteId } from '@/types';

interface RichTextEditorPaneProps {
  /** The canonical markdown body — controlled. */
  markdown: string;
  /** Called on every edit with the new serialized markdown. */
  onChange: (markdown: string) => void;
  /** When false, the editor renders read-only (used by `split` mode for the non-focused pane). */
  editable?: boolean;
  /**
   * Optional quoted text to highlight + scroll into view on arrival. Set when
   * the user navigates here from a search-result click or chat citation pill
   * that carried a snippet. Empty / undefined = no highlight.
   *
   * The pair `(quoteToHighlight, quoteHighlightKey)` is treated as a "fire
   * once per change" signal: when EITHER value changes (and quoteToHighlight
   * is non-empty), the editor applies a 10-second orange decoration and
   * scrolls the match into view. The key is typically the highlight target's
   * `requestedAt` timestamp so a re-click of the same quote on the same note
   * re-triggers the highlight.
   */
  quoteToHighlight?: string;
  /** See `quoteToHighlight`. */
  quoteHighlightKey?: number;
  /** Called once with the editor instance after it mounts, and again with
   *  `null` on unmount. Lets the owner drive imperative commands (like
   *  scroll-to-heading from the outline pane) without needing a ref forward. */
  onEditorReady?: (editor: Editor | null) => void;
  /** Entities to decorate inline. Each entity's `surfaceForms` strings are
   *  found at word boundaries in the doc and wrapped in clickable chips.
   *  Empty / undefined = no entity chips render. */
  entities?: EntityRef[];
  /** Master on/off for inline hashtag + entity chips. When false, both
   *  decorators are suppressed (hashtag via setHashtagsEnabled command;
   *  entity via passing an empty entity list). Defaults true. */
  chipsEnabled?: boolean;
  /** Magnification of the document content (1.0 = 100%). Applied as CSS `zoom`, which reflows
   *  the text at the scaled size and keeps ProseMirror's coordinate math accurate (unlike
   *  `transform: scale`). Driven by the editor's Ctrl/pinch-scroll + zoom slider. Defaults 1. */
  zoom?: number;
  /** Side margin as a percent of the pane width reserved on each side (0 = full bleed). The
   *  content column autofits the remaining width. Driven by the margin slider next to the
   *  zoom slider; applied as the `--editor-margin` CSS var on the content wrapper. */
  marginPct?: number;
  /** Controlled open-state for the Find/Replace bar. When provided (with
   *  `onFindOpenChange`), the parent owns the state — the toolbar's "Find in Note"
   *  button and the pane's Ctrl/Cmd+F handler drive the same flag. When omitted the
   *  pane manages it internally (Ctrl/Cmd+F only). */
  findOpen?: boolean;
  onFindOpenChange?: (open: boolean) => void;
  /** The current note id — pasted/dropped images upload as attachments of it. `null` for a
   *  scratch buffer (image paste falls back to inline base64, since there's nothing to attach to). */
  noteId?: NoteId | null;
  /** The shared autocomplete source store — backs `[[` wikilink completion + title→noteId
   *  resolution for the clickable wikilink chips. Optional (undefined → no completion/resolution,
   *  links render as unresolved). */
  autocompleteStore?: AutocompleteSourceStore;
}

/** How long the orange in-editor highlight stays visible. Must match the
 *  CSS keyframe duration on `.highlight-pulse-orange` (10s, app.css §incandescent-orange-pulse). */
const QUOTE_HIGHLIGHT_DURATION_MS = 10_000;

/** Trailing-debounce window for serializing the doc to markdown on edits (perf). The editor
 *  stays fully responsive while typing; downstream consumers (autosave, outline, split-sync)
 *  see the new markdown this long after the last keystroke. */
const MARKDOWN_EMIT_DEBOUNCE_MS = 250;

// Lazy-init the lowlight registry with the common-languages set (covers
// our 16-language target set: js, ts, py, sh, json, css, html, etc., plus
// the rest of highlight.js's "common" subset). Cheaper than registering
// every language; richer than starter-kit's no-highlight default.
const lowlight = createLowlight(common);

export function RichTextEditorPane({
  markdown,
  onChange,
  editable = true,
  quoteToHighlight,
  quoteHighlightKey,
  onEditorReady,
  entities,
  chipsEnabled = true,
  zoom = 1,
  marginPct = 8,
  findOpen: findOpenProp,
  onFindOpenChange,
  noteId = null,
  autocompleteStore,
}: RichTextEditorPaneProps): JSX.Element {
  const navigate = useNavigate();

  // --- Wikilink wiring (2026-06-23) -----------------------------------------------------
  // The store is read through a ref so the (stable) callbacks captured by the wikilink
  // extensions at editor-create time always see the LIVE cached title set as it refreshes.
  const storeRef = useRef<AutocompleteSourceStore | undefined>(autocompleteStore);
  storeRef.current = autocompleteStore;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;

  // Resolve a wikilink title → noteId over the cached title set (exact, case-insensitive — a
  // strict match so a click never navigates to the wrong same-prefixed note). Stable identity.
  const resolveWikilinkTitle = useCallback((title: string): string | null => {
    const titles = storeRef.current?.current().titles ?? [];
    const lower = title.trim().toLowerCase();
    const hit = titles.find((t) => t.title.toLowerCase() === lower);
    return hit ? String(hit.noteId) : null;
  }, []);
  const onWikilinkNavigate = useCallback((targetNoteId: string): void => {
    navigateRef.current(toEditor(targetNoteId));
  }, []);
  const subscribeWikilink = useCallback(
    (cb: () => void): (() => void) => storeRef.current?.subscribe(cb) ?? (() => {}),
    [],
  );
  const getWikilinkTitles = useCallback(
    () => storeRef.current?.current().titles ?? [],
    [],
  );
  // Hover-preview fetch (title + body snippet), memoized per note id so re-hovering is free.
  const wikilinkPreviewCache = useRef<Map<string, { title: string; snippet: string }>>(new Map());
  const getWikilinkPreview = useCallback(
    async (targetNoteId: string): Promise<{ title: string; snippet: string } | null> => {
      const cache = wikilinkPreviewCache.current;
      const cached = cache.get(targetNoteId);
      if (cached !== undefined) return cached;
      try {
        const { data } = await getNote({ id: asNoteId(targetNoteId), includeCompanion: false });
        const snippet = (data.body ?? '').replace(/\s+/g, ' ').trim().slice(0, 220);
        const preview = { title: data.note.title, snippet };
        cache.set(targetNoteId, preview);
        return preview;
      } catch {
        return null;
      }
    },
    [],
  );
  // Track the last markdown WE emitted so we can distinguish a prop change
  // that's our own output round-tripping (skip — would reset cursor) from
  // an external change (apply — split-mode CM6 typing, undo from elsewhere).
  const lastEmittedRef = useRef<string>(markdown);

  // The current note id, in a ref so the paste/drop handlers (closed over at editor-create
  // time) always see the live value even if the keyed remount assumption ever changes.
  const noteIdRef = useRef<NoteId | null>(noteId);
  noteIdRef.current = noteId;

  // --- Debounced markdown emit (perf, 2026-06-10) ---------------------------------------
  // The serialize-to-markdown path is the single most expensive per-keystroke operation:
  // `editor.getHTML()` walks the whole doc to an HTML string and turndown walks that back
  // to markdown (O(doc), and tables make turndown crawl every cell). Running it on every
  // keystroke is what makes large notes stutter. We defer BOTH steps off the keystroke onto
  // a trailing debounce — the editor's own ProseMirror state is the live source of truth
  // while typing; markdown is only needed downstream for autosave (itself debounced 1.5s),
  // the outline, and split-mode sync. A pending emit is flushed on blur and on unmount so
  // the save path never loses the final keystrokes.
  const pendingEditorRef = useRef<Editor | null>(null);
  const emitTimerRef = useRef<number | null>(null);

  const flushEmit = useCallback(() => {
    if (emitTimerRef.current !== null) {
      window.clearTimeout(emitTimerRef.current);
      emitTimerRef.current = null;
    }
    const ed = pendingEditorRef.current;
    if (ed === null) return;
    pendingEditorRef.current = null;
    const next = measurePerf('emit', 'md-emit', () => editorHtmlToMarkdown(ed.getHTML()));
    lastEmittedRef.current = next;
    onChange(next);
  }, [onChange]);

  const scheduleEmit = useCallback(
    (ed: Editor) => {
      pendingEditorRef.current = ed;
      if (emitTimerRef.current !== null) window.clearTimeout(emitTimerRef.current);
      emitTimerRef.current = window.setTimeout(() => {
        emitTimerRef.current = null;
        flushEmit();
      }, MARKDOWN_EMIT_DEBOUNCE_MS);
    },
    [flushEmit],
  );

  // Flush any pending emit when the pane unmounts (e.g. fast navigation away mid-type) so
  // the last edits reach the autosave path.
  useEffect(() => flushEmit, [flushEmit]);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        // We replace the bundled codeBlock with the lowlight-enabled one
        // below, and we want our own link extension config (the StarterKit
        // version doesn't have `openOnClick: false` which we need to keep
        // clicks inside the editor from navigating away mid-edit).
        codeBlock: false,
      }),
      Underline,
      Subscript,
      Superscript,
      // Horizontal text alignment (left/center/right/justify) on headings + paragraphs —
      // AND images (2026-06-23): the same toolbar align buttons now align a selected image.
      // The AttachmentImage node view reads node.attrs.textAlign and positions the image
      // (fit-content block + auto margins); markdownRoundtrip keeps the alignment as raw
      // <img style="text-align:…"> HTML so it survives the markdown round-trip.
      TextAlign.configure({ types: ['heading', 'paragraph', 'image'] }),
      TaskList,
      TaskItem.configure({ nested: true }),
      // `inclusive: false` makes the link mark NON-extending: typing (e.g. a space) at the
      // end of an autolinked URL/email ends the link instead of continuing it onto the new
      // text. Without this, autolink keeps the mark "open" so a trailing space + more text
      // stayed inside the link — most visibly for emails.
      Link.extend({ inclusive: false }).configure({
        openOnClick: false, // user clicks an edit affordance to follow
        autolink: true,
        protocols: ['http', 'https', 'mailto', 'tel'],
      }),
      // Renders `attachment:<id>` references (resolved to authed object URLs at display time)
      // AND legacy base64 `data:` images (allowBase64 kept on through the migration). See
      // attachmentImageExtension.ts — the node's `src` attribute stays the canonical reference,
      // so the markdown round-trip is byte-stable.
      AttachmentImage,
      // 2026-06-23 — Notion-style note links. `[[Title]]` renders as a clickable inline chip
      // (no visible brackets) that navigates to the target note; resolved/unresolved styling;
      // serializes back to `[[Title]]` (byte-stable). WikilinkSuggestion adds `[[` autocomplete.
      WikilinkNode.configure({
        resolveTitle: resolveWikilinkTitle,
        onNavigate: onWikilinkNavigate,
        getPreview: getWikilinkPreview,
        subscribe: subscribeWikilink,
      }),
      WikilinkSuggestion.configure({ getTitles: getWikilinkTitles }),
      // `resizable: true` ships Tiptap's COLUMN-resize handles; ResizableTableRow +
      // TableRowResize add drag-to-resize ROW heights (see tableRowResize.ts). Both are
      // editing-time only — table dimensions don't survive the markdown round-trip.
      Table.configure({ resizable: true }),
      ResizableTableRow,
      TableHeader,
      TableCell,
      TableRowResize,
      // Colored-text preservation: TextStyle is the mark, Color adds the
      // `color` attribute to it. ProseMirror's HTML parser maps inline
      // `<span style="color: …">` to this mark on the way in; turndown
      // sees the span and serialises it as raw HTML in the markdown on
      // the way out. Round-trip fidelity preserved.
      TextStyle,
      Color,
      // 2026-05-21 — `<mark>` highlight. Hand-rolled (see highlightMarkExtension)
      // because @tiptap/extension-highlight isn't a dependency. Round-trips to
      // markdown as raw inline `<mark>` HTML via the turndown keep-rule.
      HighlightMark,
      // Interactive diagrams (interactive-diagrams-v0.1.md §5.2): claims ```mermaid fences at
      // higher parse priority than CodeBlockLowlight and renders them as an editable canvas.
      // Registered BEFORE lowlight so its `pre` parse rule is collected; priority (not order)
      // decides routing, and it returns false for non-mermaid fences → lowlight handles those.
      MermaidBlock,
      CodeBlockLowlight.configure({ lowlight }),
      // 2026-05-18 — orange in-editor highlight for navigations that carry
      // a quoted snippet (search clicks, chat citations). ProseMirror
      // decoration; never mutates the document. See quoteHighlightExtension.
      QuoteHighlightExtension,
      // 2026-05-18 — inline `#hashtag` decorator. Decoration-only (no
      // document mutation); click delegation handled below via a DOM
      // listener on view.dom.
      HashtagDecoratorExtension,
      // 2026-05-18 — inline entity decorator. Data-driven (needs the
      // per-note entity surface-form list pushed via setEntityList after
      // companion query resolves). Same click-delegation path as hashtags.
      EntityDecoratorExtension,
      // 2026-05-21 — in-editor Find / Replace (Ctrl/Cmd+F). Decoration-only
      // for highlighting matches; replacement dispatches real transactions.
      // Driven by the FindReplaceBar UI below. See searchReplaceExtension.
      SearchReplaceExtension,
    ],
    content: markdownToEditorHtml(markdown),
    editable,
    onUpdate: ({ editor: ed }) => {
      // Defer the expensive HTML+markdown serialization (see scheduleEmit / flushEmit).
      scheduleEmit(ed);
    },
    onBlur: () => {
      // Losing focus flushes immediately so a click-away → save sees the latest text.
      flushEmit();
    },
    // Image paste/drop in the RICH pane → upload as an attachment and insert an
    // `attachment:<id>` image node (AttachmentImage renders it). Returning true suppresses
    // Tiptap's default base64 paste. No note id (scratch buffer) → let the default run so the
    // image still lands (inline base64) and isn't lost.
    editorProps: {
      handlePaste: (view, event) => {
        const id = noteIdRef.current;
        const items = event.clipboardData?.items;
        const imageItem = items
          ? Array.from(items).find((it) => it.type.startsWith('image/'))
          : undefined;
        if (id === null || imageItem === undefined) return false;
        const blob = imageItem.getAsFile();
        if (blob === null) return false;
        const ext = (blob.type.split('/')[1] || 'png').split('+')[0];
        void uploadAttachment(id, blob, `pasted-image.${ext}`)
          .then(({ attachmentId }) => {
            const imageType = view.state.schema.nodes.image;
            if (imageType === undefined) return;
            const node = imageType.create({ src: `attachment:${attachmentId}`, alt: 'pasted' });
            view.dispatch(view.state.tr.replaceSelectionWith(node));
          })
          .catch(() => {
            /* upload failed — the paste is simply not inserted; surfaced via the network layer */
          });
        return true;
      },
      handleDrop: (view, event) => {
        const id = noteIdRef.current;
        const files = (event as DragEvent).dataTransfer?.files;
        const imageFile = files
          ? Array.from(files).find((f) => f.type.startsWith('image/'))
          : undefined;
        if (id === null || imageFile === undefined) return false;
        event.preventDefault();
        const coords = view.posAtCoords({
          left: (event as DragEvent).clientX,
          top: (event as DragEvent).clientY,
        });
        const dropPos = coords?.pos ?? view.state.selection.from;
        void uploadAttachment(id, imageFile)
          .then(({ attachmentId }) => {
            const imageType = view.state.schema.nodes.image;
            if (imageType === undefined) return;
            const node = imageType.create({ src: `attachment:${attachmentId}`, alt: imageFile.name });
            const pos = Math.min(dropPos, view.state.doc.content.size);
            // Insert at the drop point IN ITS CONTEXT: setting a text selection at the resolved
            // position then replacing it lets the block image fit into a table cell (cell content
            // is `block+`) when the drop lands on one — a raw `tr.insert(pos)` can miss the cell.
            const tr = view.state.tr;
            const target = TextSelection.near(tr.doc.resolve(pos));
            view.dispatch(tr.setSelection(target).replaceSelectionWith(node));
          })
          .catch(() => {});
        return true;
      },
    },
  });

  // External-change handler: if the markdown prop changes to something we
  // didn't ourselves emit, reseed the editor. Tiptap's `setContent` resets
  // selection — acceptable because the only way this path fires is when
  // the user is in CM6 (split mode) and the rich pane shouldn't have the
  // cursor anyway, or when navigating to a new note (the `key={noteId}`
  // remount in EditorSurface handles that case before we get here).
  useEffect(() => {
    if (!editor) return;
    if (markdown === lastEmittedRef.current) return;
    const html = markdownToEditorHtml(markdown);
    // Tiptap v2 setContent signature: (content, emitUpdate?, parseOptions?).
    // Pass `false` for emitUpdate so this reseed doesn't trigger our own
    // onUpdate → onChange cycle (which would write back the same markdown
    // we just received and clobber lastEmittedRef tracking).
    editor.commands.setContent(html, false);
    lastEmittedRef.current = markdown;
  }, [editor, markdown]);

  // Editable toggle: split mode flips this when the focused pane changes.
  useEffect(() => {
    if (!editor) return;
    editor.setEditable(editable);
  }, [editor, editable]);

  // 2026-05-18 — expose the editor handle upward (one-way: parent gets it,
  // we don't import a ref machinery). Fired on mount + unmount so the owner
  // can hold onto a live handle for imperative commands (outline jump etc).
  useEffect(() => {
    if (onEditorReady === undefined) return;
    onEditorReady(editor ?? null);
    return () => {
      onEditorReady(null);
    };
  }, [editor, onEditorReady]);

  // 2026-05-18 — push the entity list into the EntityDecoratorExtension
  // whenever it changes (typically: companion query resolves with the
  // note's entity mentions). The extension's plugin state holds the list
  // + the derived DecorationSet; setEntityList dispatches a meta that
  // triggers a re-derivation. Empty / undefined entities → no decorations.
  //
  // The `chipsEnabled` master toggle gates the entity feed: when off,
  // we push an empty list so the entity decorator renders nothing. The
  // hashtag decorator gets a separate explicit `setHashtagsEnabled`
  // command (it's regex-driven, not data-driven, so there's no input to
  // gate by emptying — needs an explicit on/off flag).
  useEffect(() => {
    if (editor === null) return;
    editor.commands.setEntityList(chipsEnabled ? (entities ?? []) : []);
  }, [editor, entities, chipsEnabled]);

  useEffect(() => {
    if (editor === null) return;
    editor.commands.setHashtagsEnabled(chipsEnabled);
  }, [editor, chipsEnabled]);

  // 2026-05-18 — orange quote-highlight + scroll-to-match.
  //
  // The race we have to handle: this pane remounts on every note navigation
  // (via `key={noteId}` upstream in EditorSurface). On first render, the
  // editor is created with whatever `markdown` prop is at that moment —
  // typically `''` because `previewBody` is empty and `noteQuery.data` is
  // still loading. The doc is empty. setQuoteHighlight runs against an
  // empty doc, finds nothing, no-ops. Later when noteQuery resolves and
  // markdown updates, the body-sync effect calls setContent — but the
  // highlight effect's deps were [editor, quoteToHighlight, quoteHighlightKey],
  // none of which changed, so it never re-fired. Silent miss.
  //
  // Fix: include `markdown` in the deps so we re-fire after content lands,
  // AND dedupe via lastFiredHighlightKeyRef so the effect doesn't re-fire
  // on every keystroke once the user starts editing. The dedupe only marks
  // "fired" when setQuoteHighlight actually succeeded — if the doc is still
  // empty (content not yet loaded), we leave the key unmarked so a
  // subsequent markdown change retries.
  const lastFiredHighlightKeyRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (editor === null) return;
    if (quoteToHighlight === undefined || quoteToHighlight.length === 0) return;
    if (lastFiredHighlightKeyRef.current === quoteHighlightKey) return;
    const applied = editor.commands.setQuoteHighlight(quoteToHighlight);
    if (!applied) {
      // Two reasons we get here:
      //   (a) the doc isn't loaded yet (mount race) — markdown.length is
      //       small / zero; we leave lastFiredHighlightKeyRef unmarked so
      //       the next markdown change retries.
      //   (b) the doc IS loaded but our matcher couldn't find the snippet
      //       (formatting boundary, ts_headline transform mismatch, etc.).
      //       Fall back to scrolling the editor pane to the TOP — at least
      //       the user gets a deterministic starting position, not "stuck
      //       wherever the previous note's scroll was."
      if (markdown.length > 100) {
        // Doc has content but no match — fallback scroll. Mark fired so
        // we don't retry forever on every keystroke.
        const editorEl = editor.view.dom;
        let scrollParent: HTMLElement | null = editorEl.parentElement;
        while (scrollParent !== null) {
          const ov = window.getComputedStyle(scrollParent).overflowY;
          if (ov === 'auto' || ov === 'scroll') break;
          scrollParent = scrollParent.parentElement;
        }
        if (scrollParent !== null) {
          scrollParent.scrollTo({ top: 0, behavior: 'smooth' });
        }
        lastFiredHighlightKeyRef.current = quoteHighlightKey;
      }
      return;
    }
    lastFiredHighlightKeyRef.current = quoteHighlightKey;
    const handle = window.setTimeout(() => {
      editor.commands.clearQuoteHighlight();
    }, QUOTE_HIGHLIGHT_DURATION_MS);
    return () => window.clearTimeout(handle);
  }, [editor, quoteToHighlight, quoteHighlightKey, markdown]);

  // 2026-05-18 — modal state for inline-token clicks (hashtag / entity
  // decorations). When the user clicks a `.hashtag-chip` or `.entity-chip`
  // span in the editor, the delegate below captures it and opens the modal.
  const [tokenModal, setTokenModal] = useState<{
    kind: InlineTokenKind;
    label: string;
    entityId?: string;
  } | null>(null);

  // 2026-05-31 — right-click context menu for the same inline tokens. Distinct
  // from `tokenModal` (the left-click "Explore" surface) — right-click gets a
  // small floating popover at the cursor with a "Remove tag/mention" option,
  // matching OS context-menu mental model. `from`/`to` are the doc positions of
  // the chip's text content; the Remove action dispatches a delete transaction
  // over that range.
  const [tokenCtxMenu, setTokenCtxMenu] = useState<{
    kind: InlineTokenKind;
    label: string;
    x: number;
    y: number;
    from: number;
    to: number;
  } | null>(null);

  // 2026-05-21 — Find / Replace bar open state. Opened by Ctrl/Cmd+F (handler
  // below) or the toolbar's "Find in Note" button (controlled via the
  // findOpen/onFindOpenChange props), closed by Esc or the bar's close button
  // (which also clears the search decorations via the SearchReplaceExtension's
  // clearSearch command).
  const [findOpenInternal, setFindOpenInternal] = useState(false);
  const findOpen = findOpenProp ?? findOpenInternal;
  const setFindOpen = onFindOpenChange ?? setFindOpenInternal;

  const closeFind = useCallback(() => {
    setFindOpen(false);
    if (editor !== null) {
      editor.commands.clearSearch();
      // Return focus to the document so the user can keep editing.
      editor.commands.focus();
    }
  }, [editor, setFindOpen]);

  // 2026-05-21 — Ctrl/Cmd+F suppression + open, SCOPED to the editor.
  //
  // We attach the keydown listener to the editor's contentEditable root
  // (`view.dom`) rather than `window`, so the browser's native find is only
  // intercepted while the editor pane is mounted AND the keystroke originates
  // inside it (or the bar). This avoids hijacking Cmd+F app-wide. Esc closes
  // the bar from within the editor too.
  useEffect(() => {
    if (editor === null) return;
    const root = editor.view.dom;
    const onKeyDown = (e: KeyboardEvent): void => {
      const isFindCombo =
        (e.metaKey || e.ctrlKey) && !e.altKey && e.key.toLowerCase() === 'f';
      if (isFindCombo) {
        e.preventDefault();
        e.stopPropagation();
        setFindOpen(true);
        return;
      }
      if (e.key === 'Escape' && findOpen) {
        e.preventDefault();
        closeFind();
      }
    };
    root.addEventListener('keydown', onKeyDown);
    return () => {
      root.removeEventListener('keydown', onKeyDown);
    };
  }, [editor, findOpen, closeFind, setFindOpen]);

  // Click delegation on the editor's contentEditable root. Catches clicks
  // on `[data-hashtag]` / `[data-entity-id]` spans (set by the decorator
  // plugins) and opens the InlineTokenModal. Uses capture phase so the
  // editor's own click handlers (selection, link follow) don't beat us.
  useEffect(() => {
    if (editor === null) return;
    const root = editor.view.dom;
    const onClick = (e: MouseEvent): void => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;
      // Walk up at most a couple of levels — the decoration can be on the
      // span itself or on an inline child if the user clicked an emoji /
      // formatting glyph inside.
      let el: HTMLElement | null = target;
      for (let i = 0; i < 3 && el !== null; i++) {
        const hashtag = el.dataset.hashtag;
        const entityId = el.dataset.entityId;
        if (hashtag !== undefined && hashtag.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          setTokenModal({ kind: 'hashtag', label: hashtag });
          return;
        }
        if (entityId !== undefined && entityId.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          setTokenModal({
            kind: 'entity',
            label: el.textContent ?? '',
            entityId,
          });
          return;
        }
        el = el.parentElement;
      }
    };
    root.addEventListener('click', onClick, { capture: true });
    return () => {
      root.removeEventListener('click', onClick, { capture: true });
    };
  }, [editor]);

  // 2026-05-31 — `contextmenu` delegation on the same root. Mirrors the click
  // handler above but opens the InlineTokenContextMenu instead of the modal.
  // Position is captured from the mouse event so the menu lands at the chip.
  // Doc positions are resolved via `view.posAtDOM(span, 0)` (start of the
  // decoration's wrapping span) and `+ textContent.length` for the end —
  // hashtag + entity decorations are pure ProseMirror Decoration.inline()
  // wraps, so `textContent.length` matches the underlying text-node length
  // exactly.
  useEffect(() => {
    if (editor === null) return;
    const root = editor.view.dom;
    const onContextMenu = (e: MouseEvent): void => {
      const target = e.target;
      if (!(target instanceof HTMLElement)) return;
      let el: HTMLElement | null = target;
      for (let i = 0; i < 3 && el !== null; i++) {
        const hashtag = el.dataset.hashtag;
        const entityId = el.dataset.entityId;
        const isHashtag = hashtag !== undefined && hashtag.length > 0;
        const isEntity = entityId !== undefined && entityId.length > 0;
        if (isHashtag || isEntity) {
          e.preventDefault();
          e.stopPropagation();
          const chipEl = el;
          const text = chipEl.textContent ?? '';
          // posAtDOM returns the doc position at the start of the DOM node's
          // content. End = start + length of the visible text (decorations
          // span exact text ranges; no synthetic content).
          let from: number;
          try {
            from = editor.view.posAtDOM(chipEl, 0);
          } catch {
            // Defensive: if the DOM position can't be mapped (chip detached
            // mid-render or similar), bail without opening the menu.
            return;
          }
          const to = from + text.length;
          setTokenCtxMenu({
            kind: isHashtag ? 'hashtag' : 'entity',
            label: isHashtag
              ? hashtag
              : text,
            x: e.clientX,
            y: e.clientY,
            from,
            to,
          });
          return;
        }
        el = el.parentElement;
      }
    };
    root.addEventListener('contextmenu', onContextMenu, { capture: true });
    return () => {
      root.removeEventListener('contextmenu', onContextMenu, { capture: true });
    };
  }, [editor]);

  // ── Table controls (2026-06-09; redesigned 2026-06-10) ─────────────────────
  // The controls toolbar docks to the hovered table's TOP-RIGHT corner, and ALSO
  // shows while the caret sits inside a table (so keyboard users and mid-typing
  // edits keep it without reaching for the mouse). The buttons set the selection
  // into the target cell first, so add/delete act on the row/column under the
  // pointer (or the caret's cell when caret-driven).
  const paneRef = useRef<HTMLDivElement>(null);
  const [tableTool, setTableTool] = useState<{ top: number; left: number; pos: number } | null>(
    null,
  );
  const tableHideTimer = useRef<number | null>(null);
  // The table the toolbar is currently docked to + the target cell pos — kept in
  // refs so scroll/update listeners can re-dock without re-running the effect.
  const tableToolTargetRef = useRef<{ table: HTMLElement; pos: number } | null>(null);
  const tableHoverRef = useRef(false);

  useEffect(() => {
    if (editor === null) return;
    const dom = editor.view.dom as HTMLElement;

    const place = (table: HTMLElement, pos: number): void => {
      const pane = paneRef.current;
      if (pane === null) return;
      tableToolTargetRef.current = { table, pos };
      const tableRect = table.getBoundingClientRect();
      const paneRect = pane.getBoundingClientRect();
      const top = tableRect.top - paneRect.top;
      const left = tableRect.right - paneRect.left;
      // Dedupe: skip the React state update (and the pane re-render it triggers) when the
      // dock hasn't actually moved — hovering across cells of one table keeps the same
      // top-right corner, so this elides almost every per-cell re-render on a big table.
      setTableTool((prev) =>
        prev !== null &&
        prev.pos === pos &&
        Math.round(prev.top) === Math.round(top) &&
        Math.round(prev.left) === Math.round(left)
          ? prev
          : { top, left, pos },
      );
    };
    const hide = (): void => {
      tableToolTargetRef.current = null;
      setTableTool(null);
    };
    const clearHide = (): void => {
      if (tableHideTimer.current !== null) {
        window.clearTimeout(tableHideTimer.current);
        tableHideTimer.current = null;
      }
    };
    const scheduleHide = (): void => {
      clearHide();
      tableHideTimer.current = window.setTimeout(() => {
        // The caret being inside a table keeps the toolbar up after the pointer leaves.
        if (!editor.isActive('table')) hide();
      }, 200);
    };

    // Coalesce pointer tracking to one frame: mousemove fires far faster than we can
    // usefully re-dock, and posAtDOM + getBoundingClientRect per event is wasted work over
    // a dense table. Store the hovered cell and resolve it at most once per animation frame.
    let moveRaf: number | null = null;
    let pendingCell: HTMLElement | null = null;
    const flushMove = (): void => {
      moveRaf = null;
      const cell = pendingCell;
      pendingCell = null;
      if (cell === null) return;
      const table = cell.closest('table');
      if (table === null || !dom.contains(cell)) return;
      let pos: number;
      try {
        pos = editor.view.posAtDOM(cell, 0);
      } catch {
        return;
      }
      place(table as HTMLElement, pos);
    };
    const onMove = (e: MouseEvent): void => {
      const target = e.target as HTMLElement | null;
      const cell = (target?.closest('td, th') as HTMLElement | null) ?? null;
      const table = cell?.closest('table') ?? null;
      if (cell === null || table === null || !dom.contains(cell)) {
        tableHoverRef.current = false;
        scheduleHide();
        return;
      }
      tableHoverRef.current = true;
      clearHide();
      pendingCell = cell;
      if (moveRaf === null) moveRaf = window.requestAnimationFrame(flushMove);
    };
    const onLeave = (): void => {
      tableHoverRef.current = false;
      scheduleHide();
    };

    // Caret-driven dock: show/track while the selection sits inside a table.
    const onSelectionUpdate = (): void => {
      if (editor.isActive('table')) {
        const { from } = editor.state.selection;
        let node: Node | null;
        try {
          node = editor.view.domAtPos(from).node;
        } catch {
          return;
        }
        const el = (node?.nodeType === Node.TEXT_NODE ? node.parentElement : node) as
          | HTMLElement
          | null;
        const table = el?.closest?.('table') ?? null;
        if (table !== null) place(table as HTMLElement, from);
      } else if (!tableHoverRef.current) {
        hide();
      }
    };

    // Keep the dock glued to the table corner through scrolling and doc reflows —
    // rAF-coalesced so a fast scroll doesn't re-place (and re-render) per scroll event.
    let scrollRaf: number | null = null;
    const reDock = (): void => {
      if (scrollRaf !== null) return;
      scrollRaf = window.requestAnimationFrame(() => {
        scrollRaf = null;
        const target = tableToolTargetRef.current;
        if (target === null) return;
        if (!target.table.isConnected) {
          hide();
          return;
        }
        place(target.table, target.pos);
      });
    };
    const scroller = paneRef.current?.querySelector('.rich-text-pane__content') ?? null;

    dom.addEventListener('mousemove', onMove);
    dom.addEventListener('mouseleave', onLeave);
    editor.on('selectionUpdate', onSelectionUpdate);
    editor.on('update', onSelectionUpdate);
    scroller?.addEventListener('scroll', reDock, { passive: true });
    return () => {
      dom.removeEventListener('mousemove', onMove);
      dom.removeEventListener('mouseleave', onLeave);
      editor.off('selectionUpdate', onSelectionUpdate);
      editor.off('update', onSelectionUpdate);
      scroller?.removeEventListener('scroll', reDock);
      if (moveRaf !== null) window.cancelAnimationFrame(moveRaf);
      if (scrollRaf !== null) window.cancelAnimationFrame(scrollRaf);
      clearHide();
    };
  }, [editor]);

  // Run a table op against the user's ACTUAL selection when the caret is already inside a
  // table; only seed the selection from the docked cell (`tableTool.pos`) for a pure
  // hover-invoked click (caret outside the table). Every handler used to unconditionally
  // `setTextSelection(tableTool.pos)` first — but hover-tracking advances that pos cell-by-cell
  // as the pointer travels up to the corner-docked toolbar, so by click time it had usually
  // landed on the FIRST row. That collapsed the user's real row/cell selection and made
  // "Delete row" nuke row 1 instead of the highlighted row.
  const runTableOp = (apply: (chain: ReturnType<Editor['chain']>) => ReturnType<Editor['chain']>): void => {
    if (editor === null || tableTool === null) return;
    const chain = editor.chain().focus();
    if (!editor.isActive('table')) chain.setTextSelection(tableTool.pos);
    apply(chain).run();
  };

  if (!editor) {
    // First render before useEditor's internal setup completes. The shell
    // is the same shape so the layout doesn't jump when the editor mounts.
    return (
      <div className="rich-text-pane">
        <div className="rich-text-pane__content" />
      </div>
    );
  }

  return (
    <div className="rich-text-pane" ref={paneRef}>
      {/* The formatting toolbar is lifted into EditorSurface's `.ed-doc-toolbar`
          (it shares that single row with the view-mode/chips/save controls,
          rather than stacking a second bar here). The pane keeps only the
          contextual Find/Replace bar, which opens on Ctrl/Cmd+F. */}
      {findOpen && <FindReplaceBar editor={editor} onClose={closeFind} />}
      {/* On-table controls — a compact toolbar docked to the table's top-right corner.
          Appears on hover OR while the caret is inside the table; buttons target the
          hovered/caret cell's row/column via `tableTool.pos`. Column edges are draggable
          on hover for scaling (see CSS). */}
      {tableTool !== null && (
        <div
          className="table-bubble table-bubble--hover"
          role="toolbar"
          aria-label="Table controls"
          style={{ top: tableTool.top, left: tableTool.left }}
          onMouseEnter={() => {
            if (tableHideTimer.current !== null) {
              window.clearTimeout(tableHideTimer.current);
              tableHideTimer.current = null;
            }
          }}
          onMouseLeave={() => {
            if (editor !== null && !editor.isActive('table')) setTableTool(null);
          }}
        >
          <span className="table-bubble__group">
            <span className="table-bubble__label">Row</span>
            <button
              type="button"
              className="table-bubble__btn"
              title="Insert row above"
              aria-label="Insert row above"
              onClick={() => runTableOp((c) => c.addRowBefore())}
            >
              <ArrowUpToLine size={13} />
            </button>
            <button
              type="button"
              className="table-bubble__btn"
              title="Insert row below"
              aria-label="Insert row below"
              onClick={() => runTableOp((c) => c.addRowAfter())}
            >
              <ArrowDownToLine size={13} />
            </button>
            <button
              type="button"
              className="table-bubble__btn table-bubble__btn--danger"
              title="Delete row"
              aria-label="Delete row"
              onClick={() => runTableOp((c) => c.deleteRow())}
            >
              <Trash2 size={13} />
            </button>
          </span>
          <span className="table-bubble__sep" />
          <span className="table-bubble__group">
            <span className="table-bubble__label">Col</span>
            <button
              type="button"
              className="table-bubble__btn"
              title="Insert column left"
              aria-label="Insert column left"
              onClick={() => runTableOp((c) => c.addColumnBefore())}
            >
              <ArrowLeftToLine size={13} />
            </button>
            <button
              type="button"
              className="table-bubble__btn"
              title="Insert column right"
              aria-label="Insert column right"
              onClick={() => runTableOp((c) => c.addColumnAfter())}
            >
              <ArrowRightToLine size={13} />
            </button>
            <button
              type="button"
              className="table-bubble__btn table-bubble__btn--danger"
              title="Delete column"
              aria-label="Delete column"
              onClick={() => runTableOp((c) => c.deleteColumn())}
            >
              <Trash2 size={13} />
            </button>
          </span>
          <span className="table-bubble__sep" />
          <button
            type="button"
            className="table-bubble__btn"
            title="Toggle header row"
            aria-label="Toggle header row"
            onClick={() => runTableOp((c) => c.toggleHeaderRow())}
          >
            <PanelTop size={13} />
          </button>
          <button
            type="button"
            className="table-bubble__btn table-bubble__btn--danger"
            title="Delete table"
            aria-label="Delete table"
            onClick={() => runTableOp((c) => c.deleteTable())}
          >
            <XIcon size={13} />
          </button>
        </div>
      )}
      <EditorContent
        editor={editor}
        className="rich-text-pane__content"
        style={{ zoom, '--editor-margin': `${marginPct}%` } as CSSProperties}
      />
      {tokenModal !== null && (
        <InlineTokenModal
          kind={tokenModal.kind}
          label={tokenModal.label}
          entityId={tokenModal.entityId}
          onClose={() => setTokenModal(null)}
        />
      )}
      {tokenCtxMenu !== null && (
        <InlineTokenContextMenu
          kind={tokenCtxMenu.kind}
          label={tokenCtxMenu.label}
          x={tokenCtxMenu.x}
          y={tokenCtxMenu.y}
          onRemove={() => {
            // Dispatch a delete over the chip's text range. The decoration
            // disappears automatically because the underlying text is gone;
            // the decorator plugin re-runs on the next doc change.
            editor
              .chain()
              .focus()
              .deleteRange({ from: tokenCtxMenu.from, to: tokenCtxMenu.to })
              .run();
            setTokenCtxMenu(null);
          }}
          onClose={() => setTokenCtxMenu(null)}
        />
      )}
    </div>
  );
}
